/**
 * Sentry error monitoring + tracing + error-only Session Replay for the DRIS portal.
 *
 * Design notes
 * ------------
 * - DISABLED WITHOUT A DSN. `initSentry` returns immediately when `VITE_SENTRY_DSN` is absent, so
 *   local dev and any build without the DSN baked in never send events (preserves the Sentry
 *   free-tier quota). The DSN is injected as a build-arg only for the prod/staging Docker images.
 * - The DSN is a PUBLIC ingest key (not a secret) — a browser SDK must ship it in the bundle by
 *   design — so it is fine baked into the static JS.
 * - POPIA / privacy posture. This app deliberately keeps tokens out of localStorage and strips
 *   query strings from analytics; Sentry is configured to match:
 *     - Session Replay records ONLY around errors (`replaysSessionSampleRate: 0`,
 *       `replaysOnErrorSampleRate: 1.0`) — cheapest and most private.
 *     - Replay masks all text and inputs and blocks all media, so no field values or media are
 *       ever captured.
 * - Environment + release reuse the existing deploy plumbing: `VITE_ENV` (production/staging) and
 *   `VITE_GIT_COMMIT` (the deploy already passes `GIT_COMMIT=${{ github.sha }}` as a build-arg).
 */

import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';

/**
 * Initialise Sentry. Safe to call once at startup BEFORE the router/app render. No-op when
 * `VITE_SENTRY_DSN` is undefined (local dev and any build without the DSN), which keeps the
 * free-tier quota for real prod/staging traffic only.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    // production / staging — matches the tag the backend derives from ASPNETCORE_ENVIRONMENT.
    environment: import.meta.env.VITE_ENV,
    // The deploy bakes the git SHA as VITE_GIT_COMMIT; omit the key entirely when absent so Sentry
    // falls back to its own release detection rather than reporting an empty release.
    ...(import.meta.env.VITE_GIT_COMMIT
      ? { release: import.meta.env.VITE_GIT_COMMIT }
      : {}),
    tracesSampleRate: 0.2,
    // Session Replay: record ONLY around errors — never a share of normal sessions.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      // react-router v7 data-router instrumentation — attaches navigation/transaction spans to
      // the createBrowserRouter wrapped by wrapCreateBrowserRouterV7 in router.tsx.
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
  });
}
